import {
	Divider,
	IconButton,
	Stack,
	Switch,
	Typography,
	useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import i18n from "i18next";
import { useAtom, useAtomValue } from "jotai";
import { Check, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { useColorScheme } from "web_myplann/Infrastructure/Themes/ColorSchemeProvider";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { appInfoState } from "web_myplann/Shared/State/appInfoAtom";
import { userState } from "web_myplann/Shared/State/UserAtom";
import { BLOOD_RED_MYPLANN } from "web_myplann/Utils/constants";
import { device } from "web_myplann/Utils/device";
import type { Language } from "web_myplann/Utils/WebViewModel";

export const SettingsPage = () => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const appInfoAtom = useAtomValue(appInfoState);
	const { mode, setMode } = useColorScheme();

	const [userAtom, setUserAtom] = useAtom(userState);
	const [isOpen, setIsOpen] = useState(false);

	const handleChangeDarkMode = async () => {
		const enableDarkMode = mode.paletteMode !== "dark";
		if (window.ReactNativeWebView) {
			await device.switchDarkMode(enableDarkMode);
		} else {
			setMode({ paletteMode: enableDarkMode ? "dark" : "light" });
		}
	};

	const handleChangeHasBiometry = async () => {
		if (window.ReactNativeWebView) {
			const newHasBiometry = !userAtom.hasBiometry;
			const result = await device.changeBiometryStatus(newHasBiometry);

			if (result.payload.success) {
				setUserAtom({ ...userAtom, hasBiometry: newHasBiometry });
			} else {
				toast.error(
					result.payload.error ?? t(Resources.Security.BiometryError),
				);
			}
		}
	};

	const handleChangeLanguage = async (lng: Language) => {
		if (window.ReactNativeWebView) {
			await device.switchLanguage(lng);
		}

		setIsOpen(false);
	};

	const handleLogOut = async () => {
		if (window.ReactNativeWebView) {
			await device.logOut();
		}
		queryClient.clear();
		window.localStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
	};

	const handleRemoveAccount = async () => {
		if (window.ReactNativeWebView) {
			await device.removeUser();
		}
		queryClient.clear();
		window.localStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
	};

	return (
		<>
			<MyPlannLayout
				disableContentPadding
				topBar={
					<NavigationHeader
						title={t(Resources.Settings.title)}
						defaultRoute={Routing.Dashboard.path()}
					/>
				}
			>
				<Stack p={1.6} gap={0.8}>
					<Stack
						direction={"row"}
						alignItems={"center"}
						justifyContent={"space-between"}
						height={48}
					>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[700]}
						>
							{t(Resources.Settings.DarkMode)}
						</Typography>
						<Switch
							checked={theme.palette.mode === "dark"}
							onChange={handleChangeDarkMode}
						/>
					</Stack>
					<Divider sx={{ borderColor: theme.palette.deprecated.grey[200] }} />

					<Stack
						direction={"row"}
						alignItems={"center"}
						justifyContent={"space-between"}
						height={48}
					>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[700]}
						>
							{t(Resources.Security.Biometry)}
						</Typography>
						<Switch
							checked={userAtom.hasBiometry}
							onChange={handleChangeHasBiometry}
						/>
					</Stack>
					<Divider sx={{ borderColor: theme.palette.deprecated.grey[200] }} />

					<Stack
						direction={"row"}
						alignItems={"center"}
						justifyContent={"space-between"}
						height={48}
						onClick={() => setIsOpen(true)}
					>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[700]}
						>
							{t(Resources.Settings.Language)}
						</Typography>

						<ChevronRight size={16} color={theme.palette.primary.main} />
					</Stack>
					<Divider sx={{ borderColor: theme.palette.deprecated.grey[200] }} />

					<Stack
						direction={"row"}
						alignItems={"center"}
						justifyContent={"space-between"}
						height={48}
						onClick={handleLogOut}
					>
						<Typography variant="deprecatedBody1" color={BLOOD_RED_MYPLANN}>
							{t(Resources.Settings.LogOut)}
						</Typography>
					</Stack>
					<Divider sx={{ borderColor: theme.palette.deprecated.grey[200] }} />

					<Stack
						direction={"row"}
						alignItems={"center"}
						justifyContent={"space-between"}
						height={48}
						onClick={handleRemoveAccount}
					>
						<Typography variant="deprecatedBody1" color={BLOOD_RED_MYPLANN}>
							{t(Resources.Settings.RemoveAccount)}
						</Typography>
					</Stack>

					<Stack
						direction={"row"}
						justifyContent={"center"}
						mt={1}
						onClick={() => navigate(Routing.Portfolio.PortfolioList.path())}
					>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[500]}
						>
							{`${t(Resources.Settings.AppVersion)} ${appInfoAtom.appVersion}`}
						</Typography>
					</Stack>
				</Stack>
			</MyPlannLayout>

			<MyPlannDrawer
				open={isOpen}
				onClose={() => setIsOpen(false)}
				title={t(Resources.Settings.Language)}
			>
				<Stack direction="column">
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						p={1.6}
						onClick={() => handleChangeLanguage("cs")}
					>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[700]}
						>
							{t(Resources.Settings.LanguageOptions.Czech)}
						</Typography>
						{i18n.language === "cs" && (
							<IconButton>
								<Check size={20} color={theme.palette.primary.main} />
							</IconButton>
						)}
					</Stack>

					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						px={1.6}
						pb={1.6}
						onClick={() => handleChangeLanguage("en")}
					>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[700]}
						>
							{t(Resources.Settings.LanguageOptions.English)}
						</Typography>
						{i18n.language === "en" && (
							<IconButton>
								<Check size={20} color={theme.palette.primary.main} />
							</IconButton>
						)}
					</Stack>
				</Stack>
			</MyPlannDrawer>
		</>
	);
};
